import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTokens } from '../../../contexts/TokenContext';
import { useBirthInfo } from '../../../contexts/BirthInfoContext';
import { calculateFourPillars, calculateElementBalance, analyzeElements, calculateTenGods } from '../../../utils/fourPillars';
import InsufficientTokensModal from '../../../components/feature/InsufficientTokensModal';
import PayPalSubscriptionModal from '../../../components/feature/PayPalSubscriptionModal';

interface Message {
  id: number;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  actions?: { label: string; tokens: number }[];
  showReportBanner?: boolean;
  luckyDayEventType?: string;
}

interface ChatInterfaceProps {
  serviceType: 'astrology' | 'four-pillars' | 'destiny';
}

const elementKo: Record<string, string> = {
  wood: '목(木)', fire: '화(火)', earth: '토(土)', metal: '금(金)', water: '수(水)',
};

// ── 직업 적성 매핑
const careerMap: Record<string, string[]> = {
  '비견': ['창업·독립 사업', '스포츠·경쟁 직종', '영업·세일즈'],
  '겁재': ['투자·금융', '부동산·유통', '마케팅·광고'],
  '식신': ['요리·식음료업', '예술·창작 활동', '교육·강의'],
  '상관': ['방송·미디어', '예술·디자인', '컨설팅·코칭'],
  '편재': ['무역·수출입', '사업·창업', '이벤트·엔터테인먼트'],
  '정재': ['회계·세무', '금융·은행', '관리·행정직'],
  '편관': ['군·경찰·소방', '법조계·검사', '의료·보건'],
  '정관': ['공무원·공기업', '대기업 경영', '법률·행정직'],
  '편인': ['종교·철학', '연구·학술', '상담·심리치료'],
  '정인': ['교육·학문', '의료·간호', '사회복지·봉사'],
};

// ── 오행 궁합 점수
const compatScore: Record<string, Record<string, number>> = {
  '목': { '목': 70, '화': 92, '토': 50, '금': 38, '수': 88 },
  '화': { '목': 92, '화': 65, '토': 82, '금': 42, '수': 35 },
  '토': { '목': 50, '화': 82, '토': 68, '금': 88, '수': 45 },
  '금': { '목': 38, '화': 42, '토': 88, '금': 65, '수': 82 },
  '수': { '목': 88, '화': 35, '토': 45, '금': 82, '수': 70 },
};

const stemToElementMap: Record<string, string> = {
  '갑': '목', '을': '목', '병': '화', '정': '화', '무': '토',
  '기': '토', '경': '금', '신': '금', '임': '수', '계': '수',
};

function getYearElement(year: number): string {
  const heavenlyStems = ['갑', '을', '병', '정', '무', '기', '경', '신', '임', '계'];
  const stem = heavenlyStems[((year - 1900) % 10 + 10) % 10];
  return stemToElementMap[stem] ?? '목';
}

function elementHanja(e: string): string {
  const m: Record<string, string> = { '목': '木', '화': '火', '토': '土', '금': '金', '수': '水' };
  return m[e] ?? e;
}

function buildCareerAnalysis(birthDate: string, birthTime: string): string {
  try {
    const fp = calculateFourPillars(birthDate, birthTime);
    const tg = calculateTenGods(fp);
    const dominant = tg.dominantGods.slice(0, 2);
    const careers: string[] = [];
    dominant.forEach((g) => {
      const list = careerMap[g.god];
      if (list) careers.push(...list.slice(0, 2));
    });
    const unique = [...new Set(careers)].slice(0, 5);
    return `일간 ${fp.day.stem}(日干) 기준 십신 분석 결과, 가장 두드러진 기운은 **${dominant.map((g) => g.god).join(' · ')}**입니다.\n\n🎯 추천 직업군 TOP ${unique.length}\n${unique.map((c, i) => `  ${i + 1}. ${c}`).join('\n')}\n\n💡 직업 선택 포인트\n${dominant[0]?.god ? `• ${dominant[0].god}이 강한 사주는 ${careerMap[dominant[0].god]?.[0] ?? '독립적인 분야'}에서 능력을 발휘합니다.` : ''}\n${dominant[1]?.god ? `• ${dominant[1].god} 기운은 ${careerMap[dominant[1].god]?.[0] ?? '전문직'}과 잘 맞는 에너지를 줍니다.` : ''}\n\n현재 직장이 맞지 않는다고 느낀다면, 추천 직군으로의 이동을 고려해보세요!`;
  } catch {
    return '홈 화면에서 생년월일을 입력하시면 더 정확한 직업 적성 분석이 가능합니다.';
  }
}

function buildCompatibilityAnalysis(myBirthDate: string, partnerYear: number): string {
  const myYear = new Date(myBirthDate).getFullYear();
  const myElement = getYearElement(myYear);
  const partnerElement = getYearElement(partnerYear);
  const score = compatScore[myElement]?.[partnerElement] ?? 60;
  const levelLabel = score >= 85 ? '천생연분 ✨' : score >= 70 ? '좋은 인연 ☀️' : score >= 55 ? '보통 인연 🌤' : '노력 필요 🌧';
  const adviceMap: Record<number, string> = {
    92: '서로 에너지를 불어넣어 주는 최상의 궁합! 함께 있으면 두 배의 시너지가 납니다.',
    88: '깊은 신뢰와 이해를 바탕으로 성장하는 궁합. 장기적으로 매우 안정적입니다.',
    82: '서로 부족한 부분을 채워주는 보완적 관계. 갈등보다 조화가 많습니다.',
    70: '기본적으로 잘 맞는 편이지만 서로 다른 에너지를 이해하는 노력이 필요합니다.',
    65: '비슷한 성향으로 공감대는 높지만 경쟁심이 생길 수 있어요.',
    68: '안정적이고 실용적인 관계. 다만 감성적 표현을 더 많이 해주세요.',
    50: '오행 상극 관계. 서로 자극이 되기도 하지만 충돌이 잦을 수 있어요. 배려가 중요합니다.',
    45: '서로 다른 에너지로 오해가 생기기 쉽습니다. 소통을 자주 하는 것이 중요합니다.',
    42: '상충되는 기운이 있지만 서로의 차이를 인정하면 성장의 발판이 됩니다.',
    38: '어려운 궁합이지만 불가능한 것은 없습니다. 상대방의 방식을 존중하는 인내가 필요합니다.',
    35: '기운이 서로 약하게 만드는 관계. 공통 목표를 설정하면 훨씬 나아집니다.',
  };
  const advice = adviceMap[score] ?? '서로의 오행을 이해하고 보완하는 노력이 관계를 더 깊게 만들어줍니다.';
  return `${partnerYear}년생 파트너와의 오행 궁합을 분석했습니다.\n\n💫 궁합 점수: ${score}점 / 100점\n   등급: ${levelLabel}\n\n🌿 오행 관계\n   나 (${myYear}년생): ${myElement}(${elementHanja(myElement)})\n   상대 (${partnerYear}년생): ${partnerElement}(${elementHanja(partnerElement)})\n\n📝 관계 조언\n${advice}\n\n더 정확한 분석을 위해서는 상대방의 사주도 함께 고려해야 합니다. 리포트에서 자세히 확인하세요!`;
}

function buildFourPillarsAnalysis(birthDate: string, birthTime: string, birthPlace: string, gender: 'male' | 'female'): string {
  try {
    const fp = calculateFourPillars(birthDate, birthTime);
    const balance = calculateElementBalance(fp);
    const analysis = analyzeElements(balance);
    const genderText = gender === 'male' ? '남성' : '여성';
    const birthYear = new Date(birthDate).getFullYear();
    const birthMonth = new Date(birthDate).getMonth() + 1;
    const birthDay = new Date(birthDate).getDate();
    const elements = Object.entries(balance).map(([k, v]) => `${elementKo[k]} ${v}`).join(' · ');
    return `${birthPlace} 출생, ${birthYear}년 ${birthMonth}월 ${birthDay}일 ${birthTime} ${genderText}의 사주를 분석했습니다.\n\n📋 사주팔자 (四柱八字)\n  년주(年柱): ${fp.year.combined}년\n  월주(月柱): ${fp.month.combined}월\n  일주(日柱): ${fp.day.combined}일\n  시주(時柱): ${fp.hour.combined}시\n\n🌿 오행 분포\n  ${elements}\n\n✨ 핵심 분석\n일간(日干) ${fp.day.stem}이 중심이 되는 사주로, ${analysis.strongest}이 가장 강한 기운입니다.\n\n${analysis.description}\n\n가장 부족한 기운은 ${analysis.weakest}으로, 이를 보완하는 방향으로 살아가면 균형 잡힌 삶을 영위할 수 있습니다.\n\n아래 버튼으로 더 자세한 운세 차트를 확인하거나, 전체 리포트로 바로 이동할 수 있습니다.`;
  } catch {
    return '사주 정보를 기반으로 분석을 준비했습니다. 더 자세한 해석을 원하시면 아래 버튼을 눌러주세요.';
  }
}

function buildAstrologyAnalysis(birthDate: string, birthPlace: string, gender: 'male' | 'female'): string {
  const date = new Date(birthDate);
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const year = date.getFullYear();
  const genderText = gender === 'male' ? '남성' : '여성';
  const zodiacSigns = [
    { sign: '염소자리', from: [12, 22], to: [1, 19] }, { sign: '물병자리', from: [1, 20], to: [2, 18] },
    { sign: '물고기자리', from: [2, 19], to: [3, 20] }, { sign: '양자리', from: [3, 21], to: [4, 19] },
    { sign: '황소자리', from: [4, 20], to: [5, 20] }, { sign: '쌍둥이자리', from: [5, 21], to: [6, 20] },
    { sign: '게자리', from: [6, 21], to: [7, 22] }, { sign: '사자자리', from: [7, 23], to: [8, 22] },
    { sign: '처녀자리', from: [8, 23], to: [9, 22] }, { sign: '천칭자리', from: [9, 23], to: [10, 22] },
    { sign: '전갈자리', from: [10, 23], to: [11, 21] }, { sign: '사수자리', from: [11, 22], to: [12, 21] },
  ];
  const moonSigns = ['황소자리', '게자리', '전갈자리', '물고기자리', '사자자리', '물병자리'];
  const risingSign = zodiacSigns[(year + month) % 12].sign;
  const moonSign = moonSigns[day % 6];
  let sunSign = '염소자리';
  for (const z of zodiacSigns) {
    const [fm, fd] = z.from; const [tm, td] = z.to;
    if (fm === tm) continue;
    if ((month === fm && day >= fd) || (month === tm && day <= td)) { sunSign = z.sign; break; }
  }
  return `${birthPlace} 출생, ${year}년 ${month}월 ${day}일 ${genderText}의 별자리 배치를 분석했습니다.\n\n🌟 3대 주요 별자리\n  태양궁(Sun): ${sunSign} ☀️\n  달자리(Moon): ${moonSign} 🌙\n  상승궁(Ascendant): ${risingSign} ⬆️\n\n✨ 종합 해석\n태양이 ${sunSign}에 위치해 ${sunSign.includes('물') ? '깊은 감수성과 직관력을 지닌' : sunSign.includes('사자') || sunSign.includes('양') || sunSign.includes('사수') ? '열정적이고 카리스마 넘치는' : '안정적이고 신중한'} 성격입니다.\n\n달이 ${moonSign}에 있어 내면적으로는 ${moonSign.includes('전갈') || moonSign.includes('물') ? '강렬하고 깊은 감정을 품고 있으며' : moonSign.includes('황소') || moonSign.includes('게') ? '따뜻하고 포용력이 품어있으며' : '독창적이고 자유로운 감성을 지니며'}, 상승궁 ${risingSign}의 영향으로 ${risingSign.includes('사자') ? '카리스마 있고 당당하게' : risingSign.includes('물고기') || risingSign.includes('게') ? '섬세하고 친근하게' : '차분하고 신뢰감 있게'} 보입니다.\n\n아래 버튼으로 행성 배치 차트를 확인하거나, 전체 리포트로 이동하세요.`;
}

function buildFollowUpResponse(
  userMsg: string,
  serviceType: string,
  birthDate: string,
  birthTime: string,
  waitingForCompatYear: boolean,
): { content: string; isCompatWaiting: boolean } {
  if (waitingForCompatYear) {
    const yearMatch = userMsg.match(/\b(19|20)\d{2}\b/);
    if (yearMatch) {
      return { content: buildCompatibilityAnalysis(birthDate, parseInt(yearMatch[0], 10)), isCompatWaiting: false };
    }
    return { content: '태어난 연도를 숫자로 입력해주세요. 예: 1992 또는 1992년', isCompatWaiting: true };
  }

  if (userMsg.includes('직업') || userMsg.includes('적성') || userMsg.includes('커리어')) {
    if (birthDate && birthTime) {
      return { content: buildCareerAnalysis(birthDate, birthTime), isCompatWaiting: false };
    }
  }

  if (userMsg.includes('궁합') || userMsg.includes('파트너') || userMsg.includes('이성')) {
    return { content: '궁합 분석을 위해 상대방의 출생연도를 알려주세요.\n예: 1995년', isCompatWaiting: true };
  }

  const keywords: Record<string, string> = {
    연애: '현재 금성의 영향으로 감성적 교류가 활발한 시기입니다. 진솔한 표현이 관계를 깊게 만들어 줄 것입니다.',
    결혼: '배우자 궁이 안정적 기운을 받고 있어 인연이 무르익는 시기입니다. 신중하되 기회를 놓치지 마세요.',
    재물: '이번 해는 재물운이 전반적으로 상승세입니다. 투자보다는 안정적인 저축에 집중하세요.',
    건강: '수(水) 기운에 관심을 가지는 것이 중요합니다. 충분한 수분 섭취와 규칙적인 생활을 권장합니다.',
    운세: '올해 전반적인 흐름은 전반기 안정, 후반기 도약의 흐름입니다. 8월 이후에 중요한 변화가 예상됩니다.',
    인간관계: '현재 대인 기운이 활발합니다. 새로운 인연이 생길 수 있으며, 기존 관계도 깊어지는 시기입니다.',
  };

  for (const [key, response] of Object.entries(keywords)) {
    if (userMsg.includes(key)) {
      return { content: `말씀하신 ${key} 관련해서 사주를 다시 살펴보면,\n\n${response}\n\n구체적인 시기나 다른 궁금한 점이 있으시면 더 물어보세요!`, isCompatWaiting: false };
    }
  }

  const fallback = serviceType === 'four-pillars'
    ? '해당 부분의 사주를 더 세밀하게 분석해드리겠습니다. 현재 대운 주기와 연계해보면, 주요 변화가 3~5년 내 예상되며 미리 준비하면 좋은 결과를 얻을 수 있습니다.'
    : '별자리 분석을 더 깊이 살펴보면, 현재 목성의 영향을 받아 성장과 확장의 기운이 강합니다. 특히 9월~11월 사이 중요한 전환점이 예상됩니다.';
  return { content: fallback, isCompatWaiting: false };
}

const DECISION_CATEGORIES = [
  { id: 'career', label: '직장 · 커리어', icon: 'ri-briefcase-4-line', q: '직장이나 커리어 관련 중요한 결정을 앞두고 있습니다. 최적의 타이밍과 방향을 알려주세요.' },
  { id: 'love', label: '결혼 · 이별', icon: 'ri-heart-3-line', q: '결혼 또는 이별 관련 중대한 결정을 앞두고 있습니다. 사주로 보았을 때 어떤가요?' },
  { id: 'moving', label: '이사 · 이민', icon: 'ri-home-4-line', q: '이사나 이민을 고려 중입니다. 좋은 시기와 방향이 있을까요?' },
  { id: 'business', label: '창업 · 투자', icon: 'ri-funds-line', q: '창업이나 큰 투자를 결정하려 합니다. 제 사주에서 어떤 신호가 보이나요?' },
  { id: 'study', label: '학업 · 유학', icon: 'ri-graduation-cap-line', q: '학업이나 유학 결정을 앞두고 있습니다. 최적의 타이밍을 알려주세요.' },
  { id: 'health', label: '건강 · 라이프', icon: 'ri-heart-pulse-line', q: '건강이나 생활 방식의 중요한 변화를 고민 중입니다. 운세적으로 어떻게 보시나요?' },
];

const DESTINY_RESPONSES: Record<string, string> = {
  career: '직장·커리어 결정에서 가장 중요한 것은 **대운의 흐름**입니다.\n\n🎯 현재 운기 분석\n• 지금 시기는 관성(官星)이 활성화되어 있어 직업 변화가 긍정적인 신호를 보냅니다.\n• 주의할 구간: 향후 3개월 내 변화보다는 하반기 이후 도약이 권장됩니다.\n\n⭐ 최적 타이밍\n• 이직·승진: 봄 기운이 강한 시기 (목기운 월)\n• 창업·독립: 화기운이 정점에 달하는 구간\n\n⚠️ 리스크 구간\n• 현재 금극목 흐름이 있어 성급한 결정은 피하세요.\n• 동업 시 상대방의 오행도 반드시 체크하세요.\n\n투자 종류(부동산·주식·사업)를 알려주시면 맞춤 분석해드립니다!',
  love: '결혼·이별 결정에서는 **인성(印星)과 재성(財星)의 흐름**이 핵심입니다.\n\n💫 현재 애정운\n• 정관(正官)이 강한 시기로, 진지한 관계 진전에 좋은 기운이 흐릅니다.\n• 결혼 결정: 토기운(土氣運)이 안정된 구간을 선택하세요.\n\n🌹 길한 시기\n• 봄·가을이 인연의 기운이 가장 강합니다.\n• 양력 3월, 9월이 결혼 결정에 특히 좋습니다.\n\n⚠️ 주의 사항\n• 충(沖)이 일어나는 해에는 중요한 관계 결정을 서두르지 마세요.\n\n파트너의 생년도 알려주시면 궁합 분석까지 함께 해드릴게요!',
  moving: '이사·이민 결정은 **방향과 시기**가 매우 중요합니다.\n\n🏠 현재 이동운\n• 역마살(驛馬煞) 기운이 활성화되어 있어 이동·변화에 좋은 시기입니다.\n• 특히 수기운(水氣運)이 강한 당신은 북쪽 방향이 길합니다.\n\n📍 길한 방향\n• 행운 방향: 오행 기준 동쪽 혹은 북쪽\n• 피해야 할 방향: 서남쪽 (현재 흉살이 강함)\n\n📅 이사 길일\n• 음력 짝수 날, 특히 3·6·9일이 이사에 좋습니다.\n• 연간 리포트의 개인 길일 계산기를 활용해보세요.\n\n출생지와 이사 예정지를 알려주시면 더 정확하게 분석해드릴 수 있습니다.',
  business: '창업·투자 결정은 **재성(財星)의 강도와 시기**가 결과를 좌우합니다.\n\n💰 현재 재물운\n• 편재(偏財)가 활성화된 시기로, 새로운 수입원 개척에 유리합니다.\n• 단, 지금 당장의 큰 투자보다는 준비 기간으로 활용하는 것이 현명합니다.\n\n📈 최적 창업 타이밍\n• 2026년 하반기 — 토기운이 안정되어 창업 기반 구축에 최적\n• 2027년 상반기 — 화기운이 재물운을 부스팅\n\n⚠️ 주의 구간\n• 금극목이 강한 시기에는 보수적인 투자를 권장합니다.\n• 동업 시 상대방의 오행도 반드시 체크하세요.\n\n투자 종류(부동산·주식·사업)를 알려주시면 맞춤 분석해드립니다!',
  study: '학업·유학 결정에서는 **인성(印星)과 식상(食傷) 기운**이 중요합니다.\n\n📚 현재 학업운\n• 정인(正印)이 강한 시기로 새로운 지식 습득과 시험 운이 좋습니다.\n• 특히 수기운(水氣運) — 지혜와 집중력이 높은 지금이 공부하기 최적!\n\n🌍 유학 결정\n• 동쪽 방향(일본·미국 동부)이 현재 기운과 잘 맞습니다.\n• 출발 시기: 봄학기(3월)보다 가을학기(9월) 시작이 길합니다.\n\n📝 시험 길일\n• 연간 리포트의 개인 길일 계산기에서 시험 날짜를 찾아보세요!\n• 재물운이 높은 달에 시험 결과도 좋은 경향이 있습니다.\n\n목표 학교나 분야를 알려주시면 더 자세히 분석해드릴게요.',
  health: '건강·라이프 결정에서는 **오행의 균형**이 핵심입니다.\n\n💪 현재 건강운\n• 현재 일간 기운과 계절 기운이 상충하여 피로도가 높아질 수 있습니다.\n• 특히 목(木) 오행 — 간장·담낭 기관에 주의가 필요합니다.\n\n🌿 건강 관리 포인트\n• 부족한 오행을 보충하는 식단: 수기운 강화 — 검은 콩, 검은 깨\n• 행운 방향으로 산책: 동쪽이나 북쪽 방향 권장\n• 행운 컬러 착용: 초록·청색 계열\n\n⚡ 생활 방식 변화 타이밍\n• 대운이 바뀌는 시기에 맞춰 큰 변화를 주는 것이 효과적입니다.\n• 지금은 준비 기간 — 내년부터 본격 실행하세요.\n\n구체적으로 고민 중인 건강 변화를 알려주시면 맞춤 조언을 드릴게요!',
};

const DESTINY_TO_EVENT: Record<string, string> = {
  career: 'interview',
  love: 'wedding',
  moving: 'moving',
  business: 'contract',
  study: 'exam',
  health: 'exam',
};

function buildDestinyResponse(userMsg: string, birthDate: string): { content: string; eventType?: string } {
  for (const [key, response] of Object.entries(DESTINY_RESPONSES)) {
    const keywords: Record<string, string[]> = {
      career: ['직장', '커리어', '이직', '취업', '승진', '직업'],
      love: ['결혼', '이별', '연애', '파트너', '애인', '남자', '여자'],
      moving: ['이사', '이민', '이동', '집', '거주', '방향'],
      business: ['창업', '투자', '사업', '주식', '부동산', '돈'],
      study: ['학업', '공부', '시험', '유학', '학교', '입시'],
      health: ['건강', '운동', '다이어트', '병', '라이프', '생활'],
    };
    const keyList = keywords[key] ?? [];
    if (keyList.some((k) => userMsg.includes(k))) {
      return { content: response, eventType: DESTINY_TO_EVENT[key] };
    }
  }
  const fallback = `사주 오행 기반으로 해당 결정을 분석해드리겠습니다.\n\n🔮 운기 분석\n현재 당신의 운기는 변화와 전환을 준비하는 시기입니다. 성급한 결정보다는 충분한 준비와 정보 수집 후 움직이는 것이 좋습니다.\n\n⭐ 핵심 조언\n• 귀인(貴人)의 도움을 받을 수 있는 시기입니다\n• 기존 인간관계를 적극 활용하세요\n• 서두르지 말고 최적의 타이밍을 기다리세요\n\n더 구체적인 상황을 알려주시면 정밀하게 분석해드릴게요!`;
  return { content: fallback };
}

export default function ChatInterface({ serviceType }: ChatInterfaceProps) {
  const navigate = useNavigate();
  const { tokens, deductTokens, hasEnoughTokens } = useTokens();
  const { birthInfo } = useBirthInfo();
  const [analysisShown, setAnalysisShown] = useState(false);
  const [reportReady, setReportReady] = useState(false);
  const [navigatingToReport, setNavigatingToReport] = useState(false);
  const [waitingForCompatYear, setWaitingForCompatYear] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const chatStartTimeRef = useRef<number | null>(null);
  const subscriptionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const buildInitialMessages = useCallback((): Message[] => {
    if (serviceType === 'destiny') {
      const destinyGreeting = birthInfo
        ? `당신이 좋은 결정을 하길 바랍니다.\n\n${birthInfo.name ? birthInfo.name + '님의 ' : ''}사주를 바탕으로 중대 결정 분석 준비가 완료되었습니다.\n\n아래 결정 유형을 선택하거나, 현재 고민 중인 결정을 직접 말씀해 주세요. 사주 오행과 현재 운기를 바탕으로 최적의 타이밍과 방향을 제시해드리겠습니다.`
        : `당신이 좋은 결정을 하길 바랍니다.\n\n저는 운세 기반 중대 결정 비서입니다. 인생의 중요한 결정 앞에서 사주 오행과 현재 운기를 바탕으로 최적의 타이밍과 방향을 제시해드립니다.\n\n홈에서 생년월일을 입력하시면 더 정밀한 개인 맞춤 분석이 가능합니다. 지금 바로 고민 중인 결정을 아래에서 선택해보세요.`;
      return [{ id: 1, role: 'assistant', content: destinyGreeting, timestamp: new Date().toISOString() }];
    }

    if (birthInfo) {
      const analysisContent = serviceType === 'four-pillars'
        ? buildFourPillarsAnalysis(birthInfo.birthDate, birthInfo.birthTime, birthInfo.birthPlace, birthInfo.gender)
        : buildAstrologyAnalysis(birthInfo.birthDate, birthInfo.birthPlace, birthInfo.gender);
      return [
        { id: 1, role: 'assistant', content: serviceType === 'four-pillars' ? '안녕하세요! 입력하신 생년월일 정보를 바탕으로 사주팔자를 분석했습니다.' : '안녕하세요! 입력하신 생년월일 정보를 바탕으로 별자리 배치를 분석했습니다.', timestamp: new Date().toISOString() },
        { id: 2, role: 'assistant', content: analysisContent, timestamp: new Date().toISOString(), actions: [{ label: '10년 운세 차트', tokens: 30 }, { label: '30년 탄생 차트', tokens: 80 }, { label: '평생 운세 리포트', tokens: 50 }], showReportBanner: true },
      ];
    }
    return [{ id: 1, role: 'assistant', content: serviceType === 'astrology' ? '안녕하세요! 점성술 리딩을 시작합니다. 생년월일, 생시, 출생지를 알려주시면 당신의 별자리와 행성 배치를 분석해드리겠습니다.' : '안녕하세요! 사주 분석을 시작합니다. 생년월일, 생시, 출생지를 알려주시면 당신의 사주팔자를 분석해드리겠습니다.', timestamp: new Date().toISOString() }];
  }, [birthInfo, serviceType]);

  const [messages, setMessages] = useState<Message[]>(() => buildInitialMessages());
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [showInsufficientModal, setShowInsufficientModal] = useState(false);
  const [requiredTokens, setRequiredTokens] = useState(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const astrologyQuickQuestions = [
    { label: '올해 전체 운세', icon: 'ri-calendar-star-line', q: '올해 전체적인 운세 흐름이 어떤가요?' },
    { label: '연애 · 결혼운', icon: 'ri-heart-3-line', q: '연애운과 결혼운은 어떻게 나오나요?' },
    { label: '직업 적성 분석', icon: 'ri-briefcase-4-line', q: '저에게 맞는 직업 적성을 분석해주세요.' },
    { label: '재물 · 금전운', icon: 'ri-money-dollar-circle-line', q: '재물운과 금전운은 어떤가요?' },
    { label: '건강운', icon: 'ri-heart-pulse-line', q: '건강운에 대해 알려주세요.' },
    { label: '궁합 분석', icon: 'ri-user-heart-line', q: '궁합 분석을 해주세요.' },
  ];

  const destinyQuickQuestions = DECISION_CATEGORIES.map((c) => ({ label: c.label, icon: c.icon, q: c.q }));

  const quickQuestions = serviceType === 'destiny' ? destinyQuickQuestions : astrologyQuickQuestions;

  useEffect(() => {
    setMessages(buildInitialMessages());
    setAnalysisShown(!!birthInfo);
    setReportReady(!!birthInfo);
    setWaitingForCompatYear(false);
  }, [buildInitialMessages, birthInfo]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleNavigateToReport = useCallback(() => {
    setNavigatingToReport(true);
    setTimeout(() => navigate('/annual-report'), 800);
  }, [navigate]);

  useEffect(() => {
    chatStartTimeRef.current = Date.now();
    subscriptionTimerRef.current = setTimeout(() => {
      setShowSubscriptionModal(true);
    }, 60000);
    return () => {
      if (subscriptionTimerRef.current) clearTimeout(subscriptionTimerRef.current);
    };
  }, []);

  const handleSend = () => {
    if (!inputValue.trim()) return;
    const MESSAGE_COST = 5;
    if (!hasEnoughTokens(MESSAGE_COST)) {
      setRequiredTokens(MESSAGE_COST);
      setShowInsufficientModal(true);
      return;
    }
    deductTokens(MESSAGE_COST);
    const userMsg = inputValue.trim();
    const userMessage: Message = { id: messages.length + 1, role: 'user', content: userMsg, timestamp: new Date().toISOString() };
    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);

    setTimeout(() => {
      let responseContent: string;
      let newCompatWaiting = false;
      let luckyDayEventType: string | undefined;

      if (serviceType === 'destiny') {
        const result = buildDestinyResponse(userMsg, birthInfo?.birthDate ?? '');
        responseContent = result.content;
        luckyDayEventType = result.eventType;
      } else {
        const isFirstUserMsg = messages.filter((m) => m.role === 'user').length === 0;
        if (isFirstUserMsg && !birthInfo) {
          responseContent = serviceType === 'four-pillars'
            ? '감사합니다! 더 정확한 분석을 위해 홈 화면에서 생년월일, 생시, 출생지를 모두 입력하시면 자동으로 전체 사주를 계산해드립니다.'
            : '감사합니다! 더 정확한 리딩을 위해 홈 화면에서 생년월일, 생시, 출생지를 입력하시면 자동으로 정밀 분석이 진행됩니다.';
        } else {
          const result = buildFollowUpResponse(userMsg, serviceType, birthInfo?.birthDate ?? '', birthInfo?.birthTime ?? '', waitingForCompatYear);
          responseContent = result.content;
          newCompatWaiting = result.isCompatWaiting;
        }
      }

      setWaitingForCompatYear(newCompatWaiting);
      const aiMessage: Message = {
        id: messages.length + 2,
        role: 'assistant',
        content: responseContent,
        timestamp: new Date().toISOString(),
        actions: (analysisShown || serviceType === 'destiny') ? undefined : [{ label: '10년 운세 차트', tokens: 30 }, { label: '30년 탄생 차트', tokens: 80 }, { label: '평생 운세 리포트', tokens: 50 }],
        luckyDayEventType,
      };
      setMessages((prev) => [...prev, aiMessage]);
      setIsTyping(false);
      if (!analysisShown) { setAnalysisShown(true); setReportReady(true); }
    }, 1800);
  };

  const handleActionClick = (action: { label: string; tokens: number }) => {
    if (!hasEnoughTokens(action.tokens)) { setRequiredTokens(action.tokens); setShowInsufficientModal(true); return; }
    deductTokens(action.tokens);
    const generatingMsg: Message = { id: messages.length + 1, role: 'assistant', content: `${action.label} 차트를 생성하고 있습니다... 잠시만 기다려주세요.`, timestamp: new Date().toISOString() };
    setMessages((prev) => [...prev, generatingMsg]);
    setTimeout(() => {
      const doneMsg: Message = { id: messages.length + 2, role: 'assistant', content: `✅ ${action.label} 준비 완료! 운세 리포트 페이지로 이동합니다.`, timestamp: new Date().toISOString() };
      setMessages((prev) => [...prev, doneMsg]);
      setTimeout(() => navigate('/annual-report'), 1000);
    }, 1500);
  };

  const totalMessages = messages.filter((m) => m.role === 'user').length;
  const usedTokens = totalMessages * 5;
  const maxTokens = 500;
  const usagePercentage = (usedTokens / maxTokens) * 100;

  return (
    <>
      <div className="max-w-4xl mx-auto">
        {reportReady && serviceType !== 'destiny' && (
          <div className="mb-4 px-5 py-3.5 bg-gradient-to-r from-primary-gold/20 via-amber-500/15 to-primary-gold/20 border border-primary-gold/40 rounded-2xl flex items-center justify-between gap-4 animate-fade-in">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 flex items-center justify-center"><i className="ri-file-chart-fill text-primary-gold text-xl"></i></div>
              <div>
                <p className="text-primary-gold text-sm font-bold">분석 완료 — 전체 리포트 준비됨</p>
                <p className="text-purple-300/70 text-xs">차트, 십신 분석, 월별 캘린더 등 전체 내용 확인</p>
              </div>
            </div>
            <button onClick={handleNavigateToReport} disabled={navigatingToReport} className="flex-shrink-0 flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-primary-gold to-amber-500 text-primary-dark text-sm font-bold rounded-xl hover:shadow-lg hover:shadow-primary-gold/40 transition-all duration-300 whitespace-nowrap cursor-pointer disabled:opacity-70">
              {navigatingToReport ? <><i className="ri-loader-4-line animate-spin"></i><span>이동 중...</span></> : <><span>리포트 보기</span><i className="ri-arrow-right-line"></i></>}
            </button>
          </div>
        )}

        {serviceType === 'destiny' && (
          <div className="mb-4 px-5 py-3.5 bg-gradient-to-r from-primary-gold/15 via-amber-500/10 to-primary-gold/15 border border-primary-gold/30 rounded-2xl flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 flex items-center justify-center"><i className="ri-scales-3-line text-primary-gold text-xl"></i></div>
              <div>
                <p className="text-primary-gold text-sm font-bold">Destiny GPT — 결정 비서</p>
                <p className="text-purple-300/70 text-xs">직장·결혼·창업·이사 등 인생의 중대 결정을 사주 오행으로 분석</p>
              </div>
            </div>
            <button onClick={handleNavigateToReport} className="flex-shrink-0 flex items-center gap-2 px-4 py-2.5 bg-white/5 border border-primary-gold/30 text-primary-gold text-xs font-bold rounded-xl hover:bg-primary-gold/10 transition-all whitespace-nowrap cursor-pointer">
              <i className="ri-calendar-check-line text-sm"></i>
              <span>길일 계산기</span>
            </button>
          </div>
        )}

        <div className="bg-gradient-to-br from-primary-dark/90 via-primary-dark/80 to-mystic-dark/90 backdrop-blur-xl rounded-3xl border-2 border-primary-gold/40 overflow-hidden shadow-[0_0_60px_rgba(255,215,0,0.15)]">
          <div className="h-[60vh] sm:h-[600px] overflow-y-auto p-4 sm:p-8 space-y-6 custom-scrollbar">
            {messages.map((message) => (
              <div key={message.id} className={`flex gap-3 sm:gap-4 ${message.role === 'user' ? 'flex-row-reverse' : 'flex-row'} animate-fadeIn`}>
                <div className={`flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-2xl flex items-center justify-center shadow-lg ${message.role === 'assistant' ? (serviceType === 'destiny' ? 'bg-gradient-to-br from-amber-500 via-amber-600 to-yellow-700 shadow-amber-500/50' : 'bg-gradient-to-br from-purple-500 via-purple-600 to-indigo-600 shadow-purple-500/50') : 'bg-gradient-to-br from-amber-400 via-yellow-500 to-amber-600 text-primary-dark shadow-amber-500/30'}`}>
                  <i className={`${message.role === 'assistant' ? (serviceType === 'destiny' ? 'ri-scales-3-line text-white' : 'ri-magic-fill text-white') : 'ri-user-3-fill text-primary-dark'} text-lg sm:text-xl`}></i>
                </div>
                <div className={`flex-1 max-w-[90%] sm:max-w-[80%] ${message.role === 'user' ? 'text-right' : 'text-left'}`}>
                  <div className={`inline-block rounded-3xl px-5 sm:px-7 py-4 sm:py-5 shadow-xl transition-all duration-300 hover:scale-[1.01] ${message.role === 'assistant' ? 'bg-gradient-to-br from-primary-dark/95 via-mystic-dark/90 to-primary-dark/95 border-2 border-primary-gold/40 text-white shadow-primary-gold/20' : 'bg-gradient-to-br from-amber-400 via-yellow-500 to-amber-600 text-primary-dark shadow-amber-500/30'}`}>
                    <p className="text-sm sm:text-base leading-relaxed whitespace-pre-wrap font-medium">{message.content}</p>
                  </div>
                  {message.showReportBanner && (
                    <div className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary-gold/30 bg-primary-gold/10 text-primary-gold text-xs font-semibold cursor-pointer hover:bg-primary-gold/20 transition-all" onClick={handleNavigateToReport}>
                      <i className="ri-file-chart-line text-sm"></i><span>전체 리포트 바로 보기</span><i className="ri-arrow-right-line text-sm"></i>
                    </div>
                  )}
                  {/* Lucky Day Link — destiny mode responses */}
                  {message.luckyDayEventType && message.role === 'assistant' && (
                    <div className="mt-3">
                      <button
                        onClick={() => navigate(`/annual-report?event=${message.luckyDayEventType}`)}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-primary-gold/15 to-amber-500/10 border border-primary-gold/35 text-primary-gold text-xs font-bold hover:bg-primary-gold/25 hover:border-primary-gold/60 transition-all cursor-pointer whitespace-nowrap"
                      >
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-calendar-check-line text-xs"></i>
                        </div>
                        <span>이 결정에 맞는 길일 찾기</span>
                        <i className="ri-arrow-right-line text-xs"></i>
                      </button>
                    </div>
                  )}
                  {message.actions && (
                    <div className="mt-4 flex flex-col sm:flex-row flex-wrap gap-2 sm:gap-3">
                      {message.actions.map((action, index) => (
                        <button key={index} onClick={() => handleActionClick(action)} className="group relative px-5 sm:px-6 py-3 rounded-full bg-gradient-to-r from-primary-dark/80 to-mystic-dark/80 border-2 border-primary-gold/50 text-primary-gold text-sm sm:text-base font-semibold hover:border-primary-gold hover:shadow-[0_0_25px_rgba(255,215,0,0.4)] transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer overflow-hidden">
                          <div className="absolute inset-0 bg-gradient-to-r from-primary-gold/0 via-primary-gold/20 to-primary-gold/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></div>
                          <span className="relative z-10">{action.label}</span>
                          <div className="relative z-10 flex items-center gap-1 px-2 py-1 rounded-full bg-primary-gold/20"><i className="ri-vip-diamond-fill text-xs"></i><span className="text-xs font-bold">{action.tokens}</span></div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {isTyping && (
              <div className="flex gap-3 sm:gap-4 animate-fadeIn">
                <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0 ${serviceType === 'destiny' ? 'bg-gradient-to-br from-amber-500 via-amber-600 to-yellow-700 shadow-amber-500/50' : 'bg-gradient-to-br from-purple-500 via-purple-600 to-indigo-600 shadow-purple-500/50'}`}>
                  <i className={`${serviceType === 'destiny' ? 'ri-scales-3-line' : 'ri-magic-fill'} text-white text-lg sm:text-xl`}></i>
                </div>
                <div className="bg-gradient-to-br from-primary-dark/95 via-mystic-dark/90 to-primary-dark/95 border-2 border-primary-gold/40 rounded-3xl px-6 sm:px-8 py-4 sm:py-5 shadow-xl shadow-primary-gold/20">
                  <div className="flex gap-2">
                    <div className="w-2.5 h-2.5 bg-primary-gold rounded-full animate-bounce shadow-lg shadow-primary-gold/50"></div>
                    <div className="w-2.5 h-2.5 bg-primary-gold rounded-full animate-bounce shadow-lg shadow-primary-gold/50" style={{ animationDelay: '0.1s' }}></div>
                    <div className="w-2.5 h-2.5 bg-primary-gold rounded-full animate-bounce shadow-lg shadow-primary-gold/50" style={{ animationDelay: '0.2s' }}></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="border-t-2 border-primary-gold/40 p-4 sm:p-6 bg-gradient-to-br from-primary-dark/60 via-mystic-dark/50 to-primary-dark/60 backdrop-blur-lg">
            <div className="mb-4">
              <div className="flex items-center justify-between text-xs sm:text-sm text-mystic-purple mb-2">
                <span className="font-medium">토큰 사용량</span>
                <span className="font-bold">{usedTokens} / {maxTokens}</span>
              </div>
              <div className="h-2 bg-primary-dark/80 rounded-full overflow-hidden border border-primary-gold/20 shadow-inner">
                <div className="h-full bg-gradient-to-r from-amber-400 via-yellow-500 to-amber-600 rounded-full transition-all duration-500 shadow-[0_0_10px_rgba(255,215,0,0.5)]" style={{ width: `${Math.min(usagePercentage, 100)}%` }}></div>
              </div>
            </div>

            {/* Quick Question Chips */}
            <div className="flex flex-wrap gap-2 mb-4">
              {quickQuestions.map((q) => (
                <button key={q.label} onClick={() => { setInputValue(q.q); setTimeout(() => inputRef.current?.focus(), 50); }} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 border border-primary-gold/25 text-purple-200 text-xs font-medium hover:bg-primary-gold/15 hover:border-primary-gold/60 hover:text-primary-gold transition-all duration-200 whitespace-nowrap cursor-pointer">
                  <i className={`${q.icon} text-xs`}></i><span>{q.label}</span>
                </button>
              ))}
            </div>

            <div className="flex gap-3">
              <div className="flex-1 relative">
                <input ref={inputRef} type="text" value={inputValue} onChange={(e) => setInputValue(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                  placeholder={serviceType === 'destiny' ? '중대한 결정을 입력하세요... 직장, 결혼, 창업 등 (5토큰)' : waitingForCompatYear ? '상대방 출생연도 입력... 예: 1995' : reportReady ? '추가 질문 입력... 연애, 직업, 재물 등 (5토큰)' : '메시지 입력... (5토큰)'}
                  className="w-full px-5 sm:px-6 py-3.5 sm:py-4 bg-primary-dark/70 border-2 border-primary-gold/40 rounded-full text-white placeholder-gray-400 focus:border-primary-gold focus:shadow-[0_0_20px_rgba(255,215,0,0.3)] focus:outline-none text-sm sm:text-base transition-all duration-300" />
              </div>
              <button onClick={handleSend} className="w-12 h-12 sm:w-14 sm:h-14 flex-shrink-0 rounded-full bg-gradient-to-br from-amber-400 via-yellow-500 to-amber-600 text-primary-dark hover:shadow-[0_0_30px_rgba(255,215,0,0.6)] hover:scale-110 transition-all duration-300 flex items-center justify-center cursor-pointer">
                <i className="ri-send-plane-fill text-lg sm:text-xl"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      <InsufficientTokensModal isOpen={showInsufficientModal} onClose={() => setShowInsufficientModal(false)} requiredTokens={requiredTokens} currentTokens={tokens} />
      <PayPalSubscriptionModal
        isOpen={showSubscriptionModal}
        onClose={() => setShowSubscriptionModal(false)}
        chatType={serviceType === 'destiny' ? 'destiny' : serviceType === 'four-pillars' ? 'four-pillars' : 'astrology'}
      />
    </>
  );
}
